/**
 * Outreach module constants.
 * Consolidated from drawerMockData.js, influencerConfig.js, and component files.
 */

import configData from '../../../config/loader';

const OUTREACH_FALLBACK_EMAIL = 'Partnerships@radintel.ai';

/**
 * Tenant-driven default sender address prepended to the sender-email dropdown.
 * Source of truth for outreach modules. Falls back to RAD Intel.
 */
export const OUTREACH_DEFAULT_EMAIL = configData.outreachDefaultEmail || OUTREACH_FALLBACK_EMAIL;

export const DELIVERABLE_STATUSES = {
  pending: {
    label: 'Pending',
    color: 'var(--yellow-600, #9c6f22)',
    bg: 'var(--yellow-100, #f6ecd2)',
    icon: 'loading',
  },
  reviewing: {
    label: 'Reviewing',
    color: 'var(--primary-color)',
    bg: 'var(--blue-100, var(--brand-tint-100))',
    icon: 'view',
  },
  revising: {
    label: 'Revising',
    color: 'var(--red-600, #a63434)',
    bg: 'var(--red-100, #f8e3e3)',
    icon: 'task-edit',
  },
  posting: {
    label: 'Posting',
    color: 'var(--purple-600, #674da8)',
    bg: 'var(--purple-100, #eae5f6)',
    icon: 'share',
  },
  live: {
    label: 'Live',
    color: 'var(--green-600, #3d7564)',
    bg: 'var(--green-100, #ddede5)',
    icon: 'dot',
  },
  cancelled: {
    label: 'Cancelled',
    color: 'var(--red-600, #a63434)',
    bg: 'var(--red-100, #f8e3e3)',
    icon: 'cancel',
  },
};

export const INFLUENCER_STATUSES = [
  {
    label: 'AI suggested',
    value: null,
    description: 'Influencer generated by ML and does not have a status yet',
  },
  {
    label: 'Pending',
    value: 'pending',
    description: 'Influencer is pending',
  },
  {
    label: 'Recommended',
    value: 'recommended',
    description: 'Influencer is recommended, outreach has not yet started',
    trigger: 'User manually recommends influencer',
  },
  {
    label: 'Approved',
    value: 'accepted',
    description: 'Influencer is approved, outreach has not yet started',
    trigger: 'User manually approves influencer',
    nextAction: { label: 'Start Outreach', action: 'start-outreach' },
  },
  {
    label: 'Approved',
    value: 'approved',
    description: 'Influencer is approved, outreach has not yet started',
    trigger: 'User manually approves influencer',
    nextAction: { label: 'Start Outreach', action: 'start-outreach' },
  },
  {
    label: 'Negotiating',
    value: 'negotiating',
    description: 'Negotiating on pricing or deliverables, not yet accepted or rejected',
    trigger: 'User sends out the first outreach email to influencer',
    nextAction: { label: 'Send Contract', action: 'send-contract' },
  },
  {
    label: 'Declined',
    value: 'outreach-cancelled',
    description: 'Proposal has been rejected, influencer receives rejection email',
    trigger: 'User manually reject influencer',
  },
  {
    label: 'Contracting',
    value: 'contracting',
    description: 'Contract Documents sent, pending response from influencer',
    trigger: 'User sends out Influencer Contract document to influencer',
    nextAction: { label: 'View Message', action: 'message-center' },
  },
  {
    label: 'Deliverables-Confirmed',
    value: 'deliverables-confirmed',
    description: 'Brand confirmed deliverables. Influencer will start producing content',
    trigger: 'User manually confirms deliverables',
  },
  {
    label: 'Drafting',
    value: 'drafting',
    description: 'Influencer has begun producing content',
    trigger: 'User first confirmed deliverables',
    nextAction: {
      label: 'Review Media',
      action: 'review-media',
      shouldHide: (record) => {
        const { deliverables } = record;
        // only show review media action button if there are any deliverables in reviewing status
        if (Array.isArray(deliverables) && deliverables.length > 0) {
          return !deliverables.some((d) =>
            ['reviewing', 'revising', 'posting', 'live'].includes(d.status)
          );
        }
        return true;
      },
    },
  },
  {
    label: 'Posting',
    value: 'posting',
    description: 'Influencer has finished producing content, and will be posted to the channel(s)',
    trigger: 'All deliverables are in Posting status',
    nextAction: { label: 'Link Media', action: 'link-media' },
  },
  {
    label: 'Live',
    value: 'live',
    description:
      'All posts are linked in platform, metrics will be pulled into Performance Dashboard',
    trigger: 'All deliverables are in Live status',
    nextAction: { label: 'View Performance', action: 'view-performance' },
  },
];

export const SELECT_ALLOWED_STATUSES = [
  'accepted',
  'negotiating',
  'contracting',
  'drafting',
  'posting',
  'live',
];

export const getStatusByValue = (value) =>
  INFLUENCER_STATUSES.find((s) => s.value === value) || INFLUENCER_STATUSES[0];

export const AUDIENCE_COLORS = ['var(--brand-tint-100)', '#FFE0E0', '#F6FDE9', '#FCE7E1', '#F1EEEC'];

// AAPU-6711: Lickly uses soft DS brand tints for audience emoji backgrounds; other brands keep
// the original pastels. Called at render time so data-tenant is set.
export const getAudienceColors = () => {
  const isLickly =
    typeof document !== 'undefined' &&
    document.documentElement.getAttribute('data-tenant') === 'lickly';
  return isLickly
    ? [
        'var(--brand-tint-50)',
        'var(--ds-champagne-100)',
        'var(--ds-blue-100)',
        'var(--ds-green-100)',
        'var(--ds-neutral-100)',
      ]
    : AUDIENCE_COLORS;
};

export const ACCEPTED_STATUSES = ['accepted', 'posting', 'completed', 'live'];
export const HIDE_UPLOAD_STATUSES = [
  'completed',
  'approved',
  'rejected',
  'posting',
  'live',
  'cancelled',
];

/* Media upload constraints */
export const MAX_MEDIA_COUNT = 20;
export const SUPPORTED_IMAGE_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
export const SUPPORTED_VIDEO_TYPES = [
  'video/mp4',
  'video/quicktime',
  'video/x-msvideo',
  'video/x-ms-wmv',
];
export const SUPPORTED_MEDIA_TYPES = [...SUPPORTED_IMAGE_TYPES, ...SUPPORTED_VIDEO_TYPES];
